import React, { FC, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  formatDistanceToNow,
  format,
  isToday,
  isYesterday,
  isThisWeek,
} from 'date-fns';
import {
  GetSalesInboxThreadResponse,
  SalesInboxThreadEmail,
  CreateConversationReplyRequest,
  CreateConversationReplyResponse,
  CreateConversationForwardRequest,
  CreateConversationForwardResponse,
} from '@dreamai/types';
import api from '../../../api';
import EmailComponent from '../canvas/dsl-components/EmailComponent';
import OrgAvatar from '../../common/OrgAvatar';
import ErrorState from '../../common/ErrorState';
import { ReactComponent as ArrowDownIcon } from '../../../icons/arrow-down.svg';
import { ReactComponent as ArrowUpIcon } from '../../../icons/arrow-up.svg';
import { ReactComponent as ArrowLeftIcon } from '../../../icons/arrow-left.svg';
import { ReactComponent as CampaignIcon } from '../../../icons/campaign.svg';
import { ReactComponent as ReplyArrowIcon } from '../../../icons/reply-arrow.svg';
import { ReactComponent as ForwardArrowIcon } from '../../../icons/forward-arrow.svg';
import { ReactComponent as TickIcon } from '../../../icons/tick.svg';
import './sales-inbox.scss';

interface SalesInboxConversationViewProps {
  conversationId: number;
  prospect: {
    id: number;
    name: string | null;
    email: string;
    orgName: string;
  };
  campaignName: string;
  onBack: () => void;
  onReplyCreated?: () => void;
}

const SalesInboxConversationView: FC<SalesInboxConversationViewProps> = ({
  conversationId,
  prospect,
  campaignName,
  onBack,
  onReplyCreated,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const threadRef = useRef<HTMLDivElement>(null);

  // State
  const [emails, setEmails] = useState<SalesInboxThreadEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New draft added at top of thread (from reply/forward)
  const [newDraftId, setNewDraftId] = useState<string | null>(null);
  const [creatingDraft, setCreatingDraft] = useState(false);

  // Track which emails have been expanded
  const [expandedEmails, setExpandedEmails] = useState<Set<string>>(new Set());

  // Forward dropdown state
  const [showForwardDropdown, setShowForwardDropdown] = useState(false);
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardSending, setForwardSending] = useState(false);
  const [forwardSent, setForwardSent] = useState(false);

  // Fetch thread emails
  const fetchThread = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const response = await api.get<GetSalesInboxThreadResponse>(
        `/sales-inbox/conversations/${conversationId}/thread`,
      );

      if (response.data.success) {
        setEmails(response.data.emails);
      }
    } catch (err: any) {
      console.error('Error fetching thread:', err);
      setError(err.message || 'Failed to load conversation');
    } finally {
      setLoading(false);
    }
  }, [conversationId, getAccessTokenSilently]);

  useEffect(() => {
    fetchThread();
  }, [fetchThread]);

  // Mark conversation as viewed when opening
  const markAsViewed = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      await api.post(`/campaigns/conversations/${conversationId}/mark-viewed`, {
        prospectId: prospect.id,
      });
    } catch (err) {
      console.error('Error marking conversation as viewed:', err);
    }
  }, [conversationId, prospect.id, getAccessTokenSilently]);

  useEffect(() => {
    markAsViewed();
  }, [markAsViewed]);

  // Scroll to top when new draft is added
  useEffect(() => {
    if (newDraftId && threadRef.current) {
      threadRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [newDraftId]);

  // Handle creating a reply - creates draft and adds to thread
  // If the most recent message is already a draft, just expand it
  const handleCreateReply = async () => {
    // Check if the most recent email is a draft - if so, just expand it
    const sortedEmails = [...emails].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    );
    const mostRecentEmail = sortedEmails[0];

    if (mostRecentEmail?.type === 'draft' && !mostRecentEmail.isApproved) {
      // Already have a draft at the top - just expand it
      setExpandedEmails((prev) => new Set(prev).add(mostRecentEmail.id));
      if (threadRef.current) {
        threadRef.current.scrollTo({ top: 0, behavior: 'smooth' });
      }
      return;
    }

    try {
      setCreatingDraft(true);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const requestBody: CreateConversationReplyRequest = {
        suppressSignature: false,
      };

      const response = await api.post<CreateConversationReplyResponse>(
        `/agent-email-storage/conversations/${conversationId}/reply`,
        requestBody,
      );

      if (response.data.success && response.data.localEmailId) {
        // Add new draft to top of thread and auto-expand it
        setNewDraftId(response.data.localEmailId);
        setExpandedEmails((prev) =>
          new Set(prev).add(response.data.localEmailId!),
        );
        // Refetch thread to include the new draft
        await fetchThread();
        onReplyCreated?.();
      } else {
        setError(response.data.error || 'Failed to create reply');
      }
    } catch (err: any) {
      console.error('Error creating reply:', err);
      setError(err.message || 'Failed to create reply');
    } finally {
      setCreatingDraft(false);
    }
  };

  // Handle forward - sends the forward with the email from dropdown
  const handleSendForward = async () => {
    if (!forwardEmail.trim()) {
      return;
    }

    try {
      setForwardSending(true);
      const token = await getAccessTokenSilently();
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      const requestBody: CreateConversationForwardRequest = {
        to: forwardEmail.trim(),
        suppressSignature: false,
      };

      const response = await api.post<CreateConversationForwardResponse>(
        `/agent-email-storage/conversations/${conversationId}/forward`,
        requestBody,
      );

      if (response.data.success) {
        // Show success state - user can close manually
        setForwardSent(true);
      } else {
        setError(response.data.error || 'Failed to forward');
      }
    } catch (err: any) {
      console.error('Error forwarding:', err);
      setError(err.message || 'Failed to forward');
    } finally {
      setForwardSending(false);
    }
  };

  // Reset forward dropdown state when closing
  const closeForwardDropdown = () => {
    setShowForwardDropdown(false);
    setForwardEmail('');
    setForwardSent(false);
  };

  if (loading) {
    return (
      <div className='sales-inbox inset-white-backed'>
        <div className='sales-inbox-header conversation-header'>
          <button className='back-button' onClick={onBack}>
            <ArrowLeftIcon />
            <span>Back</span>
          </button>
          <div className='conversation-info'>
            <div className='skeleton-line medium' />
            <div className='skeleton-line short' />
          </div>
        </div>
        <div className='sales-inbox-conversation loading'>
          <div className='email-thread'>
            {[1, 2, 3].map((i) => (
              <div key={i} className='email-skeleton'>
                <div className='skeleton-avatar' />
                <div className='skeleton-content'>
                  <div className='skeleton-line short' />
                  <div className='skeleton-line long' />
                  <div className='skeleton-line medium' />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='sales-inbox inset-white-backed'>
        <div className='sales-inbox-header conversation-header'>
          <button className='back-button' onClick={onBack}>
            <ArrowLeftIcon />
            <span>Back</span>
          </button>
        </div>
        <div className='sales-inbox-conversation error'>
          <ErrorState title='Error loading conversation' subtitle={error} />
        </div>
      </div>
    );
  }

  // Sort emails by date descending (most recent first)
  const sortedEmails = [...emails].sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );

  // Format date for display
  const formatEmailDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isToday(date)) {
        return format(date, 'HH:mm');
      }
      if (isYesterday(date)) {
        return 'Yesterday';
      }
      if (isThisWeek(date)) {
        return format(date, 'EEEE');
      }
      return formatDistanceToNow(date, { addSuffix: true });
    } catch {
      return dateString;
    }
  };

  // Extract sender name from email address
  const getSenderName = (email: SalesInboxThreadEmail): string => {
    // For replies (from prospect), use prospect name
    if (email.type === 'reply') {
      return prospect.name || prospect.email.split('@')[0];
    }
    // For sent/draft (from us), extract name from from field or use email
    const fromMatch = email.from.match(/^([^<]+)</);
    if (fromMatch) {
      return fromMatch[1].trim();
    }
    return email.from.split('@')[0];
  };

  // Extract just the email address from from field
  const getSenderEmail = (email: SalesInboxThreadEmail): string => {
    if (email.type === 'reply') {
      return prospect.email;
    }
    const emailMatch = email.from.match(/<([^>]+)>/);
    if (emailMatch) {
      return emailMatch[1];
    }
    return email.from;
  };

  // Extract snippet from email body
  const getSnippet = (email: SalesInboxThreadEmail): string => {
    const text = email.textBody || email.htmlBody.replace(/<[^>]*>/g, '');
    return text.slice(0, 120).trim() + (text.length > 120 ? '...' : '');
  };

  // Toggle email expansion
  const toggleEmailExpanded = (emailId: string) => {
    setExpandedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(emailId)) {
        next.delete(emailId);
      } else {
        next.add(emailId);
      }
      return next;
    });
  };

  // Render collapsed email content (shared between email-row and draft-collapsed-row)
  const renderCollapsedEmailContent = (email: SalesInboxThreadEmail) => {
    const senderName = getSenderName(email);
    const isReply = email.type === 'reply';

    return (
      <>
        <OrgAvatar orgName={senderName} size={40} />
        <div className='email-row-content'>
          <div className='email-row-sender'>
            <div className='top-section'>
              <div className='left-section'>
                <span className='sender-name'>{senderName}</span>
                <span className='sender-email'>{getSenderEmail(email)}</span>
                {isReply && <span className='status-pill green'>Reply</span>}
              </div>
              <div className='right-section'>
                <div className='email-row-date'>
                  {formatEmailDate(email.date)}
                </div>
              </div>
            </div>
          </div>
          <div className='email-row-subject'>{email.subject}</div>
          <div className='email-row-snippet'>{getSnippet(email)}</div>
        </div>
      </>
    );
  };

  // Render collapsed email row
  const renderCollapsedEmail = (email: SalesInboxThreadEmail) => {
    return (
      <div
        key={email.id}
        className='email-row'
        onClick={() => toggleEmailExpanded(email.id)}
      >
        {renderCollapsedEmailContent(email)}
      </div>
    );
  };

  // Render expanded email with EmailComponent
  const renderExpandedEmail = (email: SalesInboxThreadEmail) => {
    const isDraftPendingReview = email.type === 'draft' && !email.isApproved;
    const isNewDraft = email.id === newDraftId;

    return (
      <div key={email.id} className='email-expanded-wrapper'>
        {isDraftPendingReview && (
          <div className='draft-banner'>
            <span>
              I've drafted a reply to {prospect.name || prospect.email}, but I
              need you to review it first.
            </span>
            <button
              className='review-button'
              onClick={() => toggleEmailExpanded(email.id)}
            >
              Hide reply
              <ArrowUpIcon />
            </button>
          </div>
        )}
        <EmailComponent
          emailId={email.id}
          isConversationView
          defaultEditMode={isNewDraft}
          onCollapse={
            !isDraftPendingReview
              ? () => toggleEmailExpanded(email.id)
              : undefined
          }
          onSave={() => setNewDraftId(null)}
          onSend={fetchThread}
        />
      </div>
    );
  };

  // Render draft with collapsed row or expanded EmailComponent
  const renderDraftEmail = (email: SalesInboxThreadEmail) => {
    const isExpanded = expandedEmails.has(email.id);
    const isNewDraft = email.id === newDraftId;
    const isUserDraft = email.isAgentDrafted === false;

    return (
      <div
        key={email.id}
        className={`draft-wrapper ${isUserDraft ? 'user-draft' : ''}`}
      >
        <div className='draft-banner'>
          <span>
            {isUserDraft
              ? `You have a draft in-progress to ${prospect.name || prospect.email}.`
              : `I've drafted a reply to ${prospect.name || prospect.email}, but I need you to review it first.`}
          </span>
          <button
            className='review-button'
            onClick={() => toggleEmailExpanded(email.id)}
          >
            {isExpanded ? 'Hide reply' : 'Review reply'}
            {isExpanded ? <ArrowUpIcon /> : <ArrowDownIcon />}
          </button>
        </div>
        {isExpanded ? (
          <EmailComponent
            emailId={email.id}
            isConversationView
            defaultEditMode={isNewDraft}
            onSave={() => setNewDraftId(null)}
            onSend={fetchThread}
          />
        ) : (
          <div className='draft-collapsed-row'>
            {renderCollapsedEmailContent(email)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className='sales-inbox inset-white-backed'>
      <div className='sales-inbox-header conversation-header'>
        <button className='back-button' onClick={onBack}>
          <ArrowLeftIcon />
          <span>Back</span>
        </button>
        <div className='conversation-info'>
          <h2>
            <strong>Conversation with {prospect.name || 'Unknown'}</strong>
            &nbsp; &lt;{prospect.email}&gt;
          </h2>
          <span className='campaign-badge'>
            <CampaignIcon />
            {campaignName}
          </span>
        </div>
        <div className='header-actions'>
          <button
            className='header-action-button'
            onClick={handleCreateReply}
            disabled={creatingDraft}
          >
            <ReplyArrowIcon />
            <span>Reply</span>
          </button>
          <div className='forward-dropdown-container'>
            <button
              className='header-action-button'
              onClick={() => setShowForwardDropdown(!showForwardDropdown)}
              disabled={creatingDraft}
            >
              <ForwardArrowIcon />
              <span>Forward Last Reply</span>
            </button>
            {showForwardDropdown && (
              <div className='forward-dropdown'>
                <h3>Forward to</h3>
                <input
                  type='email'
                  placeholder='Email address'
                  value={forwardEmail}
                  onChange={(e) => setForwardEmail(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSendForward();
                    }
                  }}
                  autoFocus
                />
                <div className='forward-dropdown-actions'>
                  <button className='cancel-btn' onClick={closeForwardDropdown}>
                    {forwardSent ? 'Close' : 'Cancel'}
                  </button>
                  {!forwardSent && (
                    <button
                      className='send-btn'
                      onClick={handleSendForward}
                      disabled={!forwardEmail.trim() || forwardSending}
                    >
                      {forwardSending ? <span className='spinner' /> : 'Send'}
                    </button>
                  )}
                  {forwardSent && (
                    <div className='send-success'>
                      <TickIcon />
                      <span>Sent</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <div className='sales-inbox-conversation' ref={threadRef}>
        {/* Email thread */}
        <div className='email-thread'>
          {/* Existing emails */}
          {sortedEmails.map((email) => {
            const isDraftPendingReview =
              email.type === 'draft' && !email.isApproved;

            // Drafts use shroud pattern
            if (isDraftPendingReview) {
              return renderDraftEmail(email);
            }

            const isExpanded = expandedEmails.has(email.id);

            if (isExpanded) {
              return renderExpandedEmail(email);
            }

            return renderCollapsedEmail(email);
          })}
        </div>
      </div>
    </div>
  );
};

export default SalesInboxConversationView;
